import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';

/**
 * Reusable Confirmation Dialog Component
 * @param {boolean} isOpen - Whether dialog is open
 * @param {function} onClose - Function to close dialog
 * @param {function} onConfirm - Function to execute on confirmation
 * @param {string} title - Dialog title
 * @param {string} message - Dialog message
 * @param {string} confirmText - Confirm button text (default: "Confirm")
 * @param {string} cancelText - Cancel button text (default: "Cancel")
 * @param {string} variant - Variant: "danger" | "warning" | "info" (default: "danger")
 */
export default function ConfirmDialog({
    isOpen,
    onClose,
    onConfirm,
    title = "Confirm Action",
    message = "Are you sure you want to proceed?",
    confirmText = "Confirm",
    cancelText = "Cancel",
    variant = "danger"
}) {
    const handleConfirm = async () => {
        try {
            const result = onConfirm?.();
            if (result != null && typeof result.then === 'function') {
                await result;
            }
            onClose();
        } catch {
            // Caller handles errors; keep the dialog open so the user can retry or cancel.
        }
    };

    const variantStyles = {
        danger: {
            iconBg: 'bg-rose-100',
            iconColor: 'text-rose-600',
            confirmBg: 'bg-rose-600 hover:bg-rose-700',
            border: 'border-rose-200'
        },
        warning: {
            iconBg: 'bg-amber-100',
            iconColor: 'text-amber-600',
            confirmBg: 'bg-amber-600 hover:bg-amber-700',
            border: 'border-amber-200'
        },
        info: {
            iconBg: 'bg-blue-100',
            iconColor: 'text-blue-600',
            confirmBg: 'bg-blue-600 hover:bg-blue-700',
            border: 'border-blue-200'
        }
    };

    const styles = variantStyles[variant] || variantStyles.danger;

    const renderMessage = () => {
        const messageClass =
            'text-sm text-slate-600 leading-relaxed break-words [overflow-wrap:anywhere]';

        if (typeof message !== 'string') {
            return <div className={messageClass}>{message}</div>;
        }

        return <p className={messageClass}>{message}</p>;
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity"
                        onClick={onClose}
                    />

                    {/* Dialog */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 20 }}
                        transition={{ duration: 0.3, type: "spring", bounce: 0.2 }}
                        className="relative w-full max-w-md bg-white rounded-2xl text-left overflow-hidden shadow-2xl border border-slate-200/80"
                    >
                        {/* Header */}
                        <div className={`px-6 py-5 border-b ${styles.border} bg-gradient-to-r from-slate-50 to-white`}>
                            <div className="flex items-start gap-4 min-w-0">
                                <div className={`flex-shrink-0 w-12 h-12 rounded-xl ${styles.iconBg} flex items-center justify-center`}>
                                    <AlertTriangle size={24} className={styles.iconColor} strokeWidth={2} />
                                </div>
                                <div className="flex-1 min-w-0 pr-1">
                                    <h3 className="text-lg font-bold text-slate-900 mb-1 break-words [overflow-wrap:anywhere]">
                                        {title}
                                    </h3>
                                    {renderMessage()}
                                </div>
                                <button
                                    onClick={onClose}
                                    className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 flex-shrink-0"
                                    aria-label="Close dialog"
                                >
                                    <X size={18} strokeWidth={2.5} />
                                </button>
                            </div>
                        </div>

                        {/* Footer Actions */}
                        <div className="px-6 py-4 bg-slate-50/30 flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-5 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 shadow-sm"
                            >
                                {cancelText}
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirm}
                                className={`px-5 py-2.5 text-sm font-semibold text-white ${styles.confirmBg} rounded-lg transition-all duration-200 shadow-md`}
                            >
                                {confirmText}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
